/* eslint-disable max-len */
import {
  useTranslate,
  FunctionField,
  ExportButton,
  useGetList,
  useList,
  ListContextProvider,
  List,
  useUpdate,
  useCreate
} from "react-admin";
import { useParams, Link } from "react-router-dom";
import CustomDataGrid from "./CustomDataGrid";
import { CloseIcon, DownloadIcon } from "assets/Icons";
import dayjs from "dayjs";
import * as User from "providers/user";
import ListDotsActions from "./ListDotsActions";
import {
  ListItemButton,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  useMediaQuery,
  Theme
} from "@mui/material";
import { useState } from "react";
import { getZonedDate } from "../utils/helpers";
import { CustomButton } from "./CustomButton";

export default function UserRefundList() {
  const { id } = useParams<{ id: string }>();
  const translate = useTranslate();
  const [create] = useCreate();
  const { permissions } = User.useUser();
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  const [closeFromParent, setCloseFromParent] = useState<boolean>(false);
  const [refund, setRefund] = useState<any>(null);
  const [creditValue, setCreditValue] = useState<number | "">(0);
  const [error, setError] = useState<string | null>(null);
  const [updateRefund, { isLoading: isLoadingRefund }] = useUpdate("Refund");

  const { data, isLoading } = useGetList("Refund", {
    filter: {
      user_id: id,
      company_id: permissions.companyId,
      consumed: false
    },
    sort: { field: "created_at", order: "DESC" },
    pagination: { page: 1, perPage: 100 }
  });
  const listContext = useList({ data, isLoading });

  const closeCreateDialog = () => {
    setCreditValue(0);
    setRefund(null);
    setError(null);
  };

  const takeAwayRefund = () => {
    const numericAmount = !creditValue ? 0 : Number(creditValue);

    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setError(translate("order.amount_required"));
      return;
    }
    if (numericAmount > refund?.amount) {
      setError("Amount cannot exceed refund amount");
      return;
    }
    const updatedRefundData = {
      id: refund?.id,
      data: {
        consumed: true
      }
    };

    updateRefund("Refund", updatedRefundData)
      .then(() => {
        if (numericAmount < refund?.amount) {
          create("Refund", {
            data: {
              amount: refund?.amount - numericAmount,
              consumed: false,
              appointment: refund?.appointment_id || null,
              company: refund?.company_id || refund?.company?.id,
              user: refund?.user_id || id
            }
          });
        }
      })
      .finally(() => {
        setCreditValue(0);
        setError(null);
        closeCreateDialog();
      });
  };

  const disabledUpdateRefund = () => {
    return isLoadingRefund || !refund || refund?.consumed;
  };

  return (
    <List
      empty={
        <div className="text-[14px] font-['Inter'] font-[400] text-mono-primaryTextColor">
          {translate("users.no_credits")}
        </div>
      }
      data-cy="user-refund-list"
      resource="Refund"
      filter={{ user_id: id }}
      actions={
        <div className="h-0 relative">
          <div className="absolute -top-[50px] right-0 z-10 customListActionsButtonsWrapper">
            <ExportButton
              label={translate("common.export_to_csv")}
              icon={<DownloadIcon />}
            />
          </div>
        </div>
      }
      sx={{
        marginTop: "4px",
        "& .RaList-main > div": {
          minHeight: "0px"
        },
        "& .MuiTablePagination-root": {
          display: "none"
        },
        ".customListActionsButtonsWrapper .MuiButton-sizeSmall": {
          width: "max-content",
          height: "36px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#FAF9F7",
          color: "rgba(48, 47, 46, 0.95)",
          fontSize: "14px",
          lineHeight: "18px",
          fontWeight: 500,
          borderRadius: "18px",
          padding: "0px 16px",
          border: "1px solid rgba(48, 47, 46, 0.23)",
          textTransform: "none"
        }
      }}
    >
      <ListContextProvider value={listContext}>
        <CustomDataGrid
          rowClick={false}
          {...(!isSmall
            ? {
                sx: {
                  borderRadius: "0px",
                  ".RaDatagrid-headerCell": {
                    backgroundColor: "transparent",
                    fontSize: "14px",
                    "@media screen and (max-width:1280px)": {
                      fontSize: "12px"
                    }
                  },
                  ".RaDatagrid-rowCell": {
                    borderBottom: "solid 1px rgba(16, 39, 52, 0.05)",
                    padding: "16px"
                  },
                  ".RaDatagrid-rowCell span": {
                    fontSize: "14px",
                    "@media screen and (max-width:1280px)": {
                      fontSize: "12px"
                    }
                  }
                }
              }
            : {})}
        >
          <FunctionField
            label={translate("refund.amount")}
            data-label={translate("refund.amount") + ":"}
            render={(record: any) => {
              return <div>{`$${record?.amount}`}</div>;
            }}
          />
          <FunctionField
            label={translate("refund.consumed")}
            data-label={translate("refund.consumed") + ":"}
            data-cy="refund-consumed"
            render={(record: any) => (
              <div>
                {record.consumed
                  ? translate("refund.consumed")
                  : translate("refund.not_consumed")}
              </div>
            )}
          />
          <FunctionField
            label={translate("appointment.created_at")}
            data-label={translate("appointment.created_at") + ":"}
            textAlign="right"
            render={(record: any) => (
              <div>{dayjs(record?.created_at).format("MMM D, YYYY")}</div>
            )}
          />
          <FunctionField
            label={translate("appointment.date")}
            data-label={translate("appointment.date") + ":"}
            textAlign="right"
            render={(record: any) => {
              if (!record?.appointment?.start_time) return "-";
              return (
                <div>
                  {dayjs().isSame(
                    dayjs(
                      getZonedDate(
                        record?.appointment?.start_time,
                        record?.appointment?.location?.timezone
                      )
                    ).format("MM/DD/YYYY"),
                    "day"
                  )
                    ? translate("appointment.today")
                    : dayjs(
                        getZonedDate(
                          record?.appointment?.start_time,
                          record?.appointment?.location?.timezone
                        )
                      ).format("MMM D, YYYY")}
                </div>
              );
            }}
          />
          <FunctionField
            label={translate("refund.appointment")}
            data-label={translate("refund.appointment") + ":"}
            render={(record: any) => {
              if (!record?.appointment?.id) return "-";
              return (
                <Link
                  className="flex"
                  to={`/dashboard/Appointment/${record?.appointment?.id}/show`}
                  style={{ fontSize: "inherit" }}
                >
                  <div className="font-[400] font-['Inter']">
                    {dayjs(
                      getZonedDate(
                        record?.appointment?.start_time,
                        record?.appointment?.location?.timezone
                      )
                    ).format("hh:mm A")}{" "}
                    -{" "}
                    {dayjs(
                      getZonedDate(
                        record?.appointment?.end_time,
                        record?.appointment?.location?.timezone
                      )
                    ).format("hh:mm A")}
                    ,{" "}
                    {dayjs().isSame(
                      dayjs(
                        getZonedDate(
                          record?.appointment?.start_time,
                          record?.appointment?.location?.timezone
                        )
                      ).format("MM/DD/YYYY"),
                      "day"
                    )
                      ? "Today"
                      : dayjs(
                          getZonedDate(
                            record?.appointment?.start_time,
                            record?.appointment?.location?.timezone
                          )
                        ).format("MM/DD/YYYY")}
                  </div>
                </Link>
              );
            }}
          />
          {!isSmall && !permissions.isArtist && (
            <FunctionField
              data-cy="take_away_three_dots_button"
              render={(record: any) => (
                <ListDotsActions
                  setCloseFromParent={setCloseFromParent}
                  closeFromParent={closeFromParent}
                >
                  <ListItemButton
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Inter",
                      "& .MuiListItemButton-root": {
                        fontSize: "14px",
                        fontFamily: "Inter"
                      }
                    }}
                    data-cy="take-away-refund-button"
                    onClick={() => {
                      setRefund(record);
                      setCreditValue(record?.amount);
                      setCloseFromParent(true);
                    }}
                  >
                    {translate("refund.take_away")}
                  </ListItemButton>
                </ListDotsActions>
              )}
            />
          )}
          {isSmall && !permissions.isArtist && (
            <FunctionField
              render={(record: any) => (
                <CustomButton
                  onClick={() => {
                    setRefund(record);
                    setCreditValue(record?.amount);
                    setCloseFromParent(true);
                  }}
                  cyId="take_away_three_dots_button"
                  variant="secondary"
                  text={translate("refund.take_away")}
                  sx={{ width: "100%", marginTop: "6px", marginBottom: "14px" }}
                />
              )}
            />
          )}
          {isSmall && permissions.isArtist && <div className="emptyEl" />}
        </CustomDataGrid>
        <Dialog
          open={refund !== null}
          onClose={() => setRefund(null)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          sx={{
            "& .MuiDialogContent-root": {
              padding: "0px"
            },
            "& .MuiDialog-paper": {
              borderRadius: "8px",
              backgroundColor: "#fff",
              maxWidth: "410px",
              width: "100%"
            }
          }}
        >
          <DialogTitle sx={{ display: "flex", padding: "4px 16px 0px 16px" }}>
            <div className="w-full flex justify-center items-center -mr-[42px] text-[14px] font-['Inter'] font-[600]">
              Take Away
            </div>
            <IconButton onClick={closeCreateDialog}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <div
              style={{
                backgroundColor: "#fff",
                padding: "0px 16px 16px 16px"
              }}
            >
              <div className="text-[14px] font-['Inter'] font-[400] text-mono-primaryTextColor">
                Are you sure you want to take{" "}
                {refund?.amount && (
                  <input
                    onChange={(e) => {
                      if (Number(e.target.value) > 0) {
                        setError(null);
                      }
                      if (Number(e.target.value) === 0) {
                        setCreditValue("");
                      } else {
                        setCreditValue(Number(e.target.value));
                      }
                    }}
                    value={creditValue}
                    className="w-[70px] text-center border rounded px-2 py-1"
                    min={1}
                    max={refund?.amount}
                    type="number"
                  />
                )}{" "}
                credit back?
              </div>
              {error && <p style={{ color: "red" }}>{error}</p>}
              <div className="flex justify-end mt-[16px]">
                <CustomButton
                  text={translate("common.no")}
                  variant="tertiary"
                  onClick={closeCreateDialog}
                  sx={{
                    "& .MuiButton-endIcon": {
                      marginRight: "-12px"
                    },
                    "& .MuiCircularProgress-root": {
                      display: "none"
                    },
                    "& .CustomMuiCircularProgress": {
                      display: "flex"
                    }
                  }}
                />
                <CustomButton
                  text={translate("common.yes")}
                  variant="primary"
                  cyId="take-away-refund-confirm-button"
                  onClick={() => takeAwayRefund()}
                  disabled={isLoadingRefund || !refund || refund?.consumed}
                  sx={{
                    borderRadius: "32px",
                    backgroundColor: disabledUpdateRefund()
                      ? "rgba(70, 48, 26, 0.12)"
                      : "rgba(34, 46, 66, 1)",
                    fontSize: "14px",
                    textTransform: "capitalize",
                    boxShadow: "none",
                    color: disabledUpdateRefund()
                      ? "color: rgba(0, 0, 0, 0.26);"
                      : "#fff",
                    padding: "0px 16px"
                  }}
                  endIcon={<div />}
                  submitting={isLoadingRefund}
                />
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </ListContextProvider>
    </List>
  );
}
